import { useState, useEffect } from "react";
import { postDataAPI, getDataAPI } from "../../utils/fetchData";
import { useNavigate, useLocation } from "react-router";
import LayoutHeader from "../../layout/LayoutHeader";
import usageTracker from "../../utils/usageTracker";
import { createRemindersForBooking } from "../../utils/remindersData";
import Modal from "../../components/Modal";
import { useModal } from "../../hooks/useModal";
import { markShowAdded, transitionToPhase, ONBOARDING_PHASES } from "../../utils/onboardingStateMachine";
import CustomCalendar from "../../components/Calendar/CustomCalendar";

const ManualAdd = () => {
    const [editData, setEditData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [cityOptions, setCityOptions] = useState([]);
    const [showCityDropdown, setShowCityDropdown] = useState(false);
    const [artistName, setArtistName] = useState('');
    const token = localStorage.getItem('dizeeToken');
    const navigate = useNavigate();
    const location = useLocation();
    const { modalConfig, showModal, closeModal } = useModal();
    
    // Check if we're in onboarding mode
    const searchParams = new URLSearchParams(location.search);
    const isOnboardingMode = searchParams.get('onboarding') === 'true';

    // Auto-populate artist name on component mount
    useEffect(() => {
        const fetchArtistName = async () => {
            try {
                const token = JSON.parse(localStorage.getItem('dizeeToken'));
                const response = await getDataAPI('user/getProfile', token);
                if (response.data.status === 200 && response.data.data?.artistName) {
                    const name = response.data.data.artistName;
                    setArtistName(name);
                    setEditData(prev => ({ ...prev, artist: name }));
                }
            } catch (error) {
                console.error('Error fetching artist name:', error);
            }
        };
        fetchArtistName();
    }, []);

    // Global City/State/Province auto-fill data
    const cityStateData = {
        // United States
        "New York": "NY", "Los Angeles": "CA", "Chicago": "IL", "Houston": "TX", "Phoenix": "AZ",
        "Philadelphia": "PA", "San Antonio": "TX", "San Diego": "CA", "Dallas": "TX", "San Jose": "CA",
        "Austin": "TX", "Jacksonville": "FL", "Fort Worth": "TX", "Columbus": "OH", "Charlotte": "NC",
        "San Francisco": "CA", "Indianapolis": "IN", "Seattle": "WA", "Denver": "CO", "Washington": "DC",
        "Boston": "MA", "El Paso": "TX", "Nashville": "TN", "Detroit": "MI", "Oklahoma City": "OK",
        "Portland": "OR", "Las Vegas": "NV", "Memphis": "TN", "Louisville": "KY", "Baltimore": "MD",
        "Milwaukee": "WI", "Albuquerque": "NM", "Tucson": "AZ", "Fresno": "CA", "Sacramento": "CA",
        "Mesa": "AZ", "Kansas City": "MO", "Atlanta": "GA", "Long Beach": "CA", "Colorado Springs": "CO",
        "Raleigh": "NC", "Miami": "FL", "Virginia Beach": "VA", "Omaha": "NE", "Oakland": "CA",
        "Minneapolis": "MN", "Tulsa": "OK", "Arlington": "TX", "Tampa": "FL", "New Orleans": "LA",
        
        // Canada
        "Toronto": "ON", "Montreal": "QC", "Vancouver": "BC", "Calgary": "AB", "Edmonton": "AB",
        "Ottawa": "ON", "Winnipeg": "MB", "Quebec City": "QC", "Hamilton": "ON", "Kitchener": "ON",
        "London (ON)": "ON", "Victoria": "BC", "Halifax": "NS", "Oshawa": "ON", "Windsor": "ON",
        
        // United Kingdom
        "London": "England", "Birmingham": "England", "Manchester": "England", "Glasgow": "Scotland",
        "Liverpool": "England", "Edinburgh": "Scotland", "Leeds": "England", "Sheffield": "England",
        "Bristol": "England", "Cardiff": "Wales", "Belfast": "Northern Ireland", "Newcastle (UK)": "England",
        "Nottingham": "England", "Portsmouth": "England", "Plymouth": "England",
        
        // Australia
        "Sydney": "NSW", "Melbourne": "VIC", "Brisbane": "QLD", "Perth": "WA", "Adelaide": "SA",
        "Gold Coast": "QLD", "Newcastle (AU)": "NSW", "Canberra": "ACT", "Sunshine Coast": "QLD",
        "Wollongong": "NSW", "Hobart": "TAS", "Geelong": "VIC", "Townsville": "QLD",
        
        // Germany
        "Berlin": "Berlin", "Hamburg": "Hamburg", "Munich": "Bavaria", "Cologne": "North Rhine-Westphalia",
        "Frankfurt": "Hesse", "Stuttgart": "Baden-Württemberg", "Düsseldorf": "North Rhine-Westphalia",
        "Dortmund": "North Rhine-Westphalia", "Essen": "North Rhine-Westphalia", "Leipzig": "Saxony",
        "Bremen": "Bremen", "Dresden": "Saxony", "Hanover": "Lower Saxony",
        
        // France
        "Paris": "Île-de-France", "Marseille": "Provence-Alpes-Côte d'Azur", "Lyon": "Auvergne-Rhône-Alpes",
        "Toulouse": "Occitania", "Nice": "Provence-Alpes-Côte d'Azur", "Nantes": "Pays de la Loire",
        "Strasbourg": "Grand Est", "Montpellier": "Occitania", "Bordeaux": "Nouvelle-Aquitaine",
        "Lille": "Hauts-de-France", "Rennes": "Brittany", "Reims": "Grand Est",
        
        // Italy
        "Rome": "Lazio", "Milan": "Lombardy", "Naples": "Campania", "Turin": "Piedmont",
        "Palermo": "Sicily", "Genoa": "Liguria", "Bologna": "Emilia-Romagna", "Florence": "Tuscany",
        "Bari": "Apulia", "Catania": "Sicily", "Venice": "Veneto", "Verona": "Veneto",
        
        // Spain
        "Madrid": "Community of Madrid", "Barcelona": "Catalonia", "Valencia": "Valencia",
        "Seville": "Andalusia", "Zaragoza": "Aragon", "Málaga": "Andalusia", "Murcia": "Murcia",
        "Palma": "Balearic Islands", "Las Palmas": "Canary Islands", "Bilbao": "Basque Country",
        
        // Netherlands
        "Amsterdam": "North Holland", "Rotterdam": "South Holland", "The Hague": "South Holland",
        "Utrecht": "Utrecht", "Eindhoven": "North Brabant", "Tilburg": "North Brabant",
        "Groningen": "Groningen", "Almere": "Flevoland", "Breda": "North Brabant",
        
        // Belgium
        "Brussels": "Brussels-Capital", "Antwerp": "Flanders", "Ghent": "East Flanders",
        "Charleroi": "Wallonia", "Liège": "Wallonia", "Bruges": "West Flanders",
        
        // Switzerland
        "Zurich": "Zurich", "Geneva": "Geneva", "Basel": "Basel-Stadt", "Lausanne": "Vaud",
        "Bern": "Bern", "Winterthur": "Zurich", "Lucerne": "Lucerne",
        
        // Austria
        "Vienna": "Vienna", "Graz": "Styria", "Linz": "Upper Austria", "Salzburg": "Salzburg",
        "Innsbruck": "Tyrol",
        
        // Sweden
        "Stockholm": "Stockholm", "Gothenburg": "Västra Götaland", "Malmö": "Skåne",
        "Uppsala": "Uppsala", "Västerås": "Västmanland",
        
        // Norway
        "Oslo": "Oslo", "Bergen": "Vestland", "Stavanger": "Rogaland", "Trondheim": "Trøndelag",
        
        // Denmark
        "Copenhagen": "Capital Region", "Aarhus": "Central Denmark", "Odense": "Southern Denmark",
        "Aalborg": "North Denmark",
        
        // Japan
        "Tokyo": "Tokyo", "Osaka": "Osaka", "Yokohama": "Kanagawa", "Nagoya": "Aichi",
        "Sapporo": "Hokkaido", "Fukuoka": "Fukuoka", "Kobe": "Hyogo", "Kyoto": "Kyoto",
        "Kawasaki": "Kanagawa", "Saitama": "Saitama", "Hiroshima": "Hiroshima", "Sendai": "Miyagi",
        
        // South Korea
        "Seoul": "Seoul", "Busan": "Busan", "Incheon": "Incheon", "Daegu": "Daegu",
        "Daejeon": "Daejeon", "Gwangju": "Gwangju", "Ulsan": "Ulsan",
        
        // Brazil
        "São Paulo": "São Paulo", "Rio de Janeiro": "Rio de Janeiro", "Brasília": "Federal District",
        "Salvador": "Bahia", "Fortaleza": "Ceará", "Belo Horizonte": "Minas Gerais",
        "Manaus": "Amazonas", "Curitiba": "Paraná", "Recife": "Pernambuco", "Porto Alegre": "Rio Grande do Sul",
        
        // Mexico
        "Mexico City": "Mexico City", "Guadalajara": "Jalisco", "Monterrey": "Nuevo León",
        "Puebla": "Puebla", "Tijuana": "Baja California", "León": "Guanajuato", "Juárez": "Chihuahua",
        "Zapopan": "Jalisco", "Mérida": "Yucatán", "Cancún": "Quintana Roo",
        
        // Argentina
        "Buenos Aires": "Buenos Aires", "Córdoba": "Córdoba", "Rosario": "Santa Fe",
        "Mendoza": "Mendoza", "La Plata": "Buenos Aires", "Mar del Plata": "Buenos Aires",
        
        // India
        "Mumbai": "Maharashtra", "Delhi": "Delhi", "Bangalore": "Karnataka", "Hyderabad": "Telangana",
        "Chennai": "Tamil Nadu", "Kolkata": "West Bengal", "Pune": "Maharashtra", "Ahmedabad": "Gujarat",
        "Jaipur": "Rajasthan", "Surat": "Gujarat", "Lucknow": "Uttar Pradesh", "Kanpur": "Uttar Pradesh",
        
        // China
        "Shanghai": "Shanghai", "Beijing": "Beijing", "Shenzhen": "Guangdong", "Guangzhou": "Guangdong",
        "Chengdu": "Sichuan", "Tianjin": "Tianjin", "Wuhan": "Hubei", "Dongguan": "Guangdong",
        "Chongqing": "Chongqing", "Nanjing": "Jiangsu", "Xi'an": "Shaanxi", "Hangzhou": "Zhejiang"
    };


    const handleCityChange = (e) => {
        const value = e.target.value;
        setEditData(prev => ({ ...prev, city: value }));
        
        if (value.length >= 2) {
            const matches = Object.keys(cityStateData).filter(city => 
                city.toLowerCase().includes(value.toLowerCase())
            );
            setCityOptions(matches);
            setShowCityDropdown(matches.length > 0);
        } else {
            setCityOptions([]);
            setShowCityDropdown(false);
        }
    };

    const selectCity = (city) => {
        const state = cityStateData[city];
        setEditData(prev => ({ 
            ...prev, 
            city: city,
            state: state 
        }));
        setShowCityDropdown(false);
        setCityOptions([]);
    };

    const handleNumberInput = (key, value, prefix = '') => {
        // Remove non-numeric characters except decimal point
        const numericValue = value.replace(/[^0-9.]/g, '');
        setEditData(prev => ({ ...prev, [key]: prefix + numericValue }));
    };

    const saveData = async () => {
        // Validate required fields
        const requiredFields = [
            { key: 'perf_date', label: 'Performance Date' },
            { key: 'artist', label: 'Artist' },
            { key: 'city', label: 'City' },
            { key: 'state', label: 'State/Province' },
            { key: 'venue', label: 'Venue' }
        ];

        const missingFields = requiredFields.filter(field => !editData[field.key] || editData[field.key].trim() === '');
        
        if (missingFields.length > 0) {
            const fieldNames = missingFields.map(f => f.label).join(', ');
            showModal('Required Fields Missing', `Please fill in the following required fields: ${fieldNames}`, 'error');
            return;
        }

        setIsLoading(true);
        try {
        let response = await postDataAPI('user/saveBookings', {
                data: {...editData}
        }, token);
        console.log(`response::`, response.data);
        
        // Track booking usage
        try {
            await usageTracker.trackBookingCreation('event');
        } catch (trackingError) {
            console.error('Usage tracking failed:', trackingError);
        }
        
        // Create booking reminders
        try {
            if (response.data?.data?._id || response.data?.data?.id) {
                const bookingId = response.data.data._id || response.data.data.id;
                const bookingData = {
                    ...editData,
                    _id: bookingId,
                    created_at: new Date().toISOString()
                };
                console.log('🎫 Creating reminders for new booking:', bookingId);
                const reminders = createRemindersForBooking(bookingId, bookingData);
                console.log(`✅ Created ${reminders.length} booking reminders`);
            }
        } catch (reminderError) {
            console.error('Error creating booking reminders:', reminderError);
            // Don't block the user flow if reminders fail
        }
        
        // If onboarding mode, mark as complete and navigate to home
        if (isOnboardingMode) {
            markShowAdded();
            setTimeout(() => {
                navigate('/');
            }, 1500);
        } else {
            setTimeout(() => {
                navigate('/bookings/view-all');
            }, 1500);
        }
        } catch (error) {
            console.error('Error saving booking:', error);
            showModal('Save Error', 'Error saving booking. Please try again.', 'error');
        }
        setIsLoading(false);
    }

    // Required fields first, then optional fields
    const allBasicFields = [
        // Required fields first
        { key: 'artist', label: 'Artist', placeholder: 'Enter artist name', required: true },
        { key: 'perf_date', label: 'Performance Date', placeholder: 'Enter date', type: 'date', required: true },
        { key: 'city', label: 'City', placeholder: 'Enter city name', required: true },
        { key: 'state', label: 'State/Province', placeholder: 'Auto-filled', required: true },
        { key: 'venue', label: 'Venue', placeholder: 'Enter venue name', required: true },
        // Optional fields
        { key: 'status', label: 'Status', placeholder: 'Select status', type: 'select', options: ['pending', 'confirmed', 'announced', 'private', 'canceled'] },
        { key: 'show_time', label: 'Set Time', placeholder: 'Enter set time (e.g., 9:00 PM)', type: 'text', required: false },
        { key: 'announce_date', label: 'Announce Date', placeholder: 'Enter announce date', type: 'date' },
        { key: 'on_sale_date', label: 'On Sale Date', placeholder: 'Enter on sale date', type: 'date' },
        { key: 'event_title', label: 'Event Title', placeholder: 'Enter event title' },
        { key: 'guarantee', label: 'Guarantee', placeholder: 'Enter amount', type: 'currency' },
        { key: 'walkout_potential', label: 'Walkout', placeholder: 'Enter amount', type: 'currency' },
        { key: 'sellable_cap', label: 'Sellable Cap', placeholder: 'Enter capacity', type: 'number', max: 1000000 },
        { key: 'current_ticket_count', label: 'Current Ticket Count', placeholder: 'Enter current tickets sold', type: 'number' },
        { key: 'promoter', label: 'Promoter', placeholder: 'Enter promoter' },
    ];
    
    // Essential fields for onboarding mode (only Status, Venue, City, Performance Date, Fee/Guarantee)
    // Note: We'll also add notes field to advancedFields filtering
    const onboardingBasicFields = ['status', 'venue', 'city', 'perf_date', 'guarantee'];
    
    // Filter fields based on mode
    const basicFields = isOnboardingMode 
        ? allBasicFields.filter(field => onboardingBasicFields.includes(field.key))
        : allBasicFields;

    const allAdvancedFields = [
        { key: 'appearing_with', label: 'Lineup', placeholder: 'Enter lineup information' },
        { key: 'red_flag', label: 'Red Flag', type: 'checkbox' },
        { key: 'red_flag_notes', label: 'Red Flag Notes', placeholder: 'Enter red flag details', type: 'textarea' },
        { key: 'promoter_company', label: 'Promoter Company', placeholder: 'Enter promoter company' },
        { key: 'promoter_contact_names', label: 'Promoter Contact Name(s)', placeholder: 'Enter contact names' },
        { key: 'promoter_contact_emails', label: 'Promoter Contact Email(s)', placeholder: 'Enter contact emails' },
        { key: 'venue_category', label: 'Venue Category', placeholder: 'Enter venue category' },
        { key: 'billing_type', label: 'Billing Type', placeholder: 'Enter billing type' },
        { key: 'territory', label: 'Territory', placeholder: 'Enter territory' },
        { key: 'advanced_info', label: 'Advanced Info', placeholder: 'Enter advanced information', type: 'textarea' },
        { key: 'deal_type', label: 'Deal Type', placeholder: 'Enter deal type (flat, bonus, etc.)' },
        { key: 'deal_info', label: 'Deal Info', placeholder: 'Enter deal information' },
        { key: 'gross_potential', label: 'Gross Potential', placeholder: 'Enter gross potential' },
        { key: 'net_potential', label: 'Net Potential', placeholder: 'Enter net potential' },
        { key: 'artist_net', label: 'Artist Net', placeholder: 'Enter artist net' },
        { key: 'artist_profit', label: 'Artist Profit', placeholder: 'Enter artist profit' },
        { key: 'split_point', label: 'Split Point', placeholder: 'Enter split point' },
        { key: 'amount_to_split', label: 'Amount to Split', placeholder: 'Enter amount to split' },
        { key: 'ticket_notes', label: 'Ticket Notes', placeholder: 'Enter ticket notes', type: 'textarea' },
        { key: 'ticket_prices_tiers', label: 'Ticket Prices (tiers)', placeholder: 'Enter ticket price tiers' },
        { key: 'ticket_taxes_fees', label: 'Ticket Taxes / Fees', placeholder: 'Enter taxes and fees' },
        { key: 'total_fees', label: 'Total Fees', placeholder: 'Enter total fees' },
        { key: 'deposit_due', label: 'Deposit Due', placeholder: 'Enter deposit due' },
        { key: 'deposit_received', label: 'Deposit Received', placeholder: 'Enter deposit received' },
        { key: 'contract_status', label: 'Contract Status', placeholder: 'Enter contract status' },
        { key: 'currency_conversion', label: 'Currency / Conversion', placeholder: 'Enter currency info' },
        { key: 'additional_clauses', label: 'Additional Clauses', placeholder: 'Enter additional clauses', type: 'textarea' },
        { key: 'schedule', label: 'Schedule', placeholder: 'Enter schedule details' },
        { key: 'notes', label: 'Notes', placeholder: 'Enter additional notes', type: 'textarea' }
    ];
    
    // For onboarding, only show notes field in advanced section
    const advancedFields = isOnboardingMode 
        ? allAdvancedFields.filter(field => field.key === 'notes')
        : allAdvancedFields;

    const renderField = (field) => {
        const value = editData[field.key] || '';

        // Special handling for city field with dropdown
        if (field.key === 'city') {
            return (
                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)] transition-all relative">
                    <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                        {field.label}
                        {field.required && <span className="text-[#FFFF00] ml-1">*</span>}
                    </label>
                    <input
                        type="text"
                        value={value}
                        onChange={handleCityChange}
                        className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                        placeholder={field.placeholder}
                    />
                    
                    {/* City Dropdown */}
                    {showCityDropdown && cityOptions.length > 0 && (
                        <div className="absolute z-10 top-full left-0 right-0 mt-2 overflow-hidden rounded-lg shadow-lg">
                            <div className="max-h-[150px] overflow-y-auto bg-[#1A1A1A] border border-[rgba(255,255,255,0.1)]">
                                {cityOptions.map((city, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-3 hover:bg-[rgba(255,255,0,0.1)] cursor-pointer transition-colors border-b border-[rgba(255,255,255,0.05)] last:border-b-0"
                                        onClick={() => selectCity(city)}
                                    >
                                        <div className="text-white text-[12px]">{city}</div>
                                        <div className="text-[rgba(255,255,255,0.7)] text-[10px]">{cityStateData[city]}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        // Special handling for currency fields
        if (field.type === 'currency') {
            return (
                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)] transition-all">
                    <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                        {field.label}
                        {field.required && <span className="text-[#FFFF00] ml-1">*</span>}
                    </label>
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => handleNumberInput(field.key, e.target.value, '$')}
                        className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                        placeholder={field.placeholder}
                    />
                </div>
            );
        }

        // Special handling for number fields
        if (field.type === 'number') {
            return (
                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)] transition-all">
                    <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                        {field.label}
                        {field.required && <span className="text-[#FFFF00] ml-1">*</span>}
                    </label>
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => handleNumberInput(field.key, e.target.value)}
                        className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                        placeholder={field.placeholder}
                        max={field.max}
                    />
                </div>
            );
        }

        // Select dropdown fields
        if (field.type === 'select') {
            return (
                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)] transition-all">
                    <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                        {field.label}
                        {field.required && <span className="text-[#FFFF00] ml-1">*</span>}
                    </label>
                    <select
                        value={value}
                        onChange={(e) => setEditData(prev => ({ ...prev, [field.key]: e.target.value }))}
                        className="w-full bg-transparent border-none outline-none text-white text-[12px] appearance-none cursor-pointer"
                        style={{ backgroundImage: 'none' }}
                    >
                        <option value="" className="bg-black text-white">Select {field.label.toLowerCase()}</option>
                        {field.options.map((option, index) => (
                            <option key={index} value={option} className="bg-black text-white">
                                {option.charAt(0).toUpperCase() + option.slice(1)}
                            </option>
                        ))}
                    </select>
                </div>
            );
        }

        // Checkbox field
        if (field.type === 'checkbox') {
            return (
                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)] transition-all">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-[14px] h-[14px] overflow-hidden">
                            <input
                                type="checkbox"
                                checked={value || false}
                                onChange={(e) => setEditData(prev => ({ ...prev, [field.key]: e.target.checked }))}
                                className="accent-[#FFFF00] cursor-pointer"
                                style={{ width: '14px', height: '14px', minWidth: '14px', minHeight: '14px', maxWidth: '14px', maxHeight: '14px' }}
                            />
                        </div>
                        <label className="text-white text-[12px] cursor-pointer" style={{ fontWeight: 300 }} onClick={() => setEditData(prev => ({ ...prev, [field.key]: !prev[field.key] }))}>
                            {field.label}
                        </label>
                        {value && (
                            <span className="text-[#FFFF00] text-[12px]">✓ Flagged</span>
                        )}
                    </div>
                </div>
            );
        }

        // Textarea fields
        if (field.type === 'textarea') {
            return (
                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)] transition-all">
                    <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                        {field.label}
                        {field.required && <span className="text-[#FFFF00] ml-1">*</span>}
                    </label>
                    <textarea
                        value={value}
                        onChange={(e) => setEditData(prev => ({ ...prev, [field.key]: e.target.value }))}
                        className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] resize-none h-20"
                        placeholder={field.placeholder}
                    />
                </div>
            );
        }

        // Date field with CustomCalendar
        if (field.type === 'date') {
            return (
                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)] transition-all">
                    <CustomCalendar
                        label={field.label + (field.required ? ' *' : '')}
                        value={value}
                        onChange={(date) => setEditData(prev => ({ ...prev, [field.key]: date }))}
                        placeholder={field.placeholder}
                        showTime={false}
                        required={false}
                        theme="bookings"
                    />
                </div>
            );
        }

        // Regular input fields
        return (
            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)] transition-all">
                <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                    {field.label}
                    {field.required && <span className="text-[#FFFF00] ml-1">*</span>}
                </label>
                <input
                    type={field.type || 'text'}
                    value={value}
                    onChange={(e) => setEditData(prev => ({ ...prev, [field.key]: e.target.value }))}
                    className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                    placeholder={field.placeholder}
                    readOnly={field.readOnly}
                />
            </div>
        );
    };

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between items-center">
                        <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Manual Add</span>
                        <div className="flex items-center gap-4">
                            <span 
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                onClick={saveData}
                            >
                                Save
                            </span>
                            
                            <span className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" onClick={() => navigate(-1)}>Go back</span>
                        </div>
                    </div>
                    
                    {/* Help Text */}
                    <div className="px-4 mb-6 w-full">
                        <div className="text-[10px] text-[rgba(255,255,255,0.7)] text-left">
                            Fill in the required fields to create your booking entry.
                        </div>
                    </div>

                    <div className="w-full px-4 flex-1 overflow-y-auto">
                        <div className="flex flex-col">
                            {isOnboardingMode && (
                            <div className="mb-6">
                                <span className="text-[14px] text-white" style={{ fontWeight: 300 }}>
                                        Add Your First Show
                                </span>
                                <p className="text-[10px] text-[rgba(255,255,255,0.7)] mt-1">
                                        Fill in the essential fields below. You can add more details later!
                                </p>
                        </div>
                            )}

                            {/* Basic Fields */}
                            <div className="space-y-4 mb-6">
                                {basicFields.map(renderField)}
                    </div>

                            {/* Advanced Section Toggle */}
                            <div className="mb-4">
                                <button
                                    onClick={() => setShowAdvanced(!showAdvanced)}
                                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)] transition-all flex items-center justify-between"
                                >
                                    <span className="text-white text-[12px]" style={{ fontWeight: 300 }}>Advanced Fields</span>
                                    <svg 
                                        className={`w-4 h-4 text-white transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                        </div>

                            {/* Advanced Fields */}
                            {showAdvanced && (
                                <div className="space-y-4 mb-6">
                                    <div className="text-[10px] text-[rgba(255,255,255,0.7)] mb-4">
                                        Advanced booking details for comprehensive tracking and AI integration
                        </div>
                                    {advancedFields.map(renderField)}
                    </div>
                            )}

                            {/* Save Button */}
                            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)] transition-all">
                                <button
                                    onClick={saveData}
                                    disabled={isLoading}
                                    className="w-full bg-[#FFFF00] text-black text-[12px] font-semibold py-3 rounded-lg hover:bg-[rgba(255,255,0,0.9)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                >
                                    {isLoading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        'Save Booking'
                                    )}
                                </button>
                    </div>
                        </div>

                    </div>
                </div>
                </div>
                
            <Modal
                isOpen={modalConfig.isOpen}
                onClose={closeModal}
                onConfirm={modalConfig.onConfirm}
                title={modalConfig.title}
                message={modalConfig.message}
                type={modalConfig.type}
                showCancel={modalConfig.showCancel}
                confirmText={modalConfig.confirmText}
                cancelText={modalConfig.cancelText}
            />
        </LayoutHeader>
    );
};

export default ManualAdd;